import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuthContext } from '../../../auth/context/AuthContext';

/**
 * Memory Modal Component
 * Shows all stored memories for the user with ability to view and delete
 */
const MemoryModal = ({ isOpen, onClose, userId }) => {
  const { user } = useAuthContext();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingMemoryId, setDeletingMemoryId] = useState(null);

  // Get the display name (preferred name or first name)
  const displayName = useMemo(() => {
    return user?.preferredName || user?.firstName || 'You';
  }, [user?.preferredName, user?.firstName]);

  // Replace "User" with the user's actual name in memory facts
  const formatMemoryFact = (fact) => {
    if (!fact) return fact;
    // Replace "User" at the start of sentences or standalone
    return fact.replace(/\bUser\b/g, displayName);
  };

  const formatMemoryDate = (memory) => {
    const raw = memory.lastUpdated || memory.firstRecordedAt;
    if (!raw) return null;
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  // Fetch user memories when modal opens
  useEffect(() => {
    console.log('🧠 MemoryModal: useEffect triggered - isOpen:', isOpen, 'userId:', userId);
    if (isOpen && userId) {
      fetchMemories();
    } else if (isOpen && !userId) {
      console.log('🧠 MemoryModal: Modal is open but userId is missing!');
      setError('User ID not available');
    }
  }, [isOpen, userId]);

  const fetchMemories = async () => {
    console.log('🧠 MemoryModal: fetchMemories called');
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      console.log('🧠 MemoryModal: token exists:', !!token);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const response = await axios.get(`${apiUrl}/memory/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('🧠 MemoryModal: API response:', response.data);
      
      if (response.data.success) {
        console.log('🧠 MemoryModal: Setting memories:', response.data.memories?.length || 0, 'items');
        setMemories(response.data.memories || []);
      } else {
        console.error('🧠 MemoryModal: API returned failure:', response.data);
        setError('Failed to load memories');
      }
    } catch (err) {
      console.error('🧠 MemoryModal: Error fetching memories:', err);
      console.error('🧠 MemoryModal: Error response:', err.response?.data);
      setError('Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const deleteMemory = async (memoryId, memoryText) => {
    if (!window.confirm(`Are you sure you want to delete this memory?\n\n"${formatMemoryFact(memoryText)}"`)) {
      return;
    }

    setDeletingMemoryId(memoryId);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const response = await axios.delete(`${apiUrl}/memory/${memoryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Remove from local state
        setMemories(prev => prev.filter(memory => memory._id !== memoryId));
      } else {
        setError('Failed to delete memory');
      }
    } catch (err) {
      console.error('Error deleting memory:', err);
      setError('Failed to delete memory');
    } finally {
      setDeletingMemoryId(null);
    }
  };

  const clearAllMemories = async () => {
    if (!window.confirm(`Are you sure you want to delete ALL ${memories.length} memories?\n\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const response = await axios.delete(`${apiUrl}/memory/clear`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setMemories([]);
      } else {
        setError('Failed to clear memories');
      }
    } catch (err) {
      console.error('Error clearing memories:', err);
      setError('Failed to clear memories');
    } finally {
      setLoading(false);
    }
  };



  if (!isOpen) {
    console.log('🧠 MemoryModal: Modal is closed, returning null');
    return null;
  }
  
  console.log('🧠 MemoryModal: Rendering modal, userId:', userId, 'memories count:', memories.length);

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[9999]">
      <div className="absolute inset-0 bg-white/50 dark:bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div 
        className="memory-modal-squircle relative border border-gray-200 dark:border-[#282828] bg-white dark:bg-[#181818] shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Saved Memories
          </h2>
          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('🧠 MemoryModal: Close button (X) clicked');
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="mx-6 border-b border-gray-200 dark:border-[#282828]"></div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300 dark:border-gray-300"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-300">Loading memories...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {!loading && !error && memories.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No memories saved yet</p>
             
            </div>
          )}

          {!loading && !error && memories.length > 0 && (
            <div className="space-y-0">
              {memories.map((memory, index) => (
                <div
                  key={memory._id || index}
                  className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-[#282828] last:border-b-0"
                >
                  <div className="flex-1 pr-4 min-w-0">
                    <p className="text-gray-900 dark:text-white">
                      {formatMemoryFact(memory.fact)}
                    </p>
                    {formatMemoryDate(memory) && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Saved {formatMemoryDate(memory)}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => deleteMemory(memory._id, memory.fact)}
                    disabled={deletingMemoryId === memory._id}
                    className="email-squircle-btn-sm p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 flex-shrink-0"
                    title="Delete this memory"
                  >
                    {deletingMemoryId === memory._id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                    ) : (
                      <TrashIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {memories.length > 0 && (
          <>
            <div className="mx-6 border-t border-gray-200 dark:border-[#282828]"></div>
            <div className="px-3 py-3 flex justify-center">
              <button
                onClick={clearAllMemories}
                disabled={loading}
                className="email-squircle-btn px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
              >
                Clear All
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MemoryModal;
